import React, { ReactNode, useState } from 'react';
import { mui } from '~/tools/mui';
import { Severity } from '~/interfaces/survey.interface';

type AlertProps = {
  open?: boolean;
  title?: string | null;
  list?: (string | number)[];
  severity?: Severity;
  onClick?: Function;
  onClose?: Function;
  children?: ReactNode;
};

/**
 * @component Alert
 *
 * Wraps Material UI's Alert component to standardize some styles/behaviors
 * across our application.
 *
 * @param {boolean} open - if Alert is open/visible by default
 * @param {string} [title] - if defined, will be displayed in AlertTitle above content
 * @param {array} [list] - if defined, will display a bullet-point list under content
 * @param {any} [children] - arbitrary content
 * @param {string} severity - one of "success", "error", "warning", or "info" (affects color + icon displayed)
 * @param {function} [onClick] - if defined, will be called if/when the user clicks anywhere on the Alert
 * @param {function} [onClose] - if defined, will be caleld if/when the user closes the Alert and displays a "close" icon
 *
 * NOTE: other props can be passed as defined by Material UI's Alert component.
 *
 * @see https://mui.com/components/alert/
 * @see https://mui.com/api/alert/
 */
const Alert = ({
  open = true,
  title = null,
  list = [],
  severity = Severity.Info,
  onClick = () => {},
  onClose = () => {},
  children,
  ...alertProps
}: AlertProps) => {
  const [isOpen, setIsOpen] = useState(open);

  const handleClickClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <mui.Collapse in={isOpen}>
      <mui.Alert severity={severity} onClose={handleClickClose} onClick={handleClick} {...alertProps}>
        {title && <mui.AlertTitle>{title}</mui.AlertTitle>}

        <mui.Typography variant="body2">{children}</mui.Typography>

        {list && list.length > 0 && (
          <ul>
            {list.map((value, i) => (
              <li key={i}>{value}</li>
            ))}
          </ul>
        )}
      </mui.Alert>
    </mui.Collapse>
  );
};

export default Alert;
